/**
 * Bounding-box utilities for the multi-region pipeline.
 *
 * The pipeline used to run each region's algorithm against the full source
 * image and discard everything outside the labelMap. For pages with several
 * small regions that's wasteful - the algorithm touches every pixel of the
 * full source even when only a tiny rectangle ends up visible. These helpers
 * let the pipeline crop down to the bbox of each region's labelled pixels
 * before running, then composite the bbox-shaped result back into the
 * full-size accumulator.
 *
 * `computeLabelBbox` walks the labelMap once and returns the tight rectangle
 * containing every pixel whose red channel equals the requested label, or
 * null if no pixel matches (empty region - pipeline can skip).
 *
 * `cropImageData` produces an independent ImageData from a sub-rectangle of
 * a larger one, copying RGBA bytes verbatim.
 */

export interface Bbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Compute the tight bounding box of pixels in `labelMap` whose red-channel
 * value equals `label`. Single pass, O(width × height).
 *
 * Returns null when the label is absent so the pipeline can skip the region
 * without allocating a zero-sized buffer.
 */
export function computeLabelBbox(labelMap: ImageData, label: number): Bbox | null {
  const w = labelMap.width;
  const h = labelMap.height;
  const data = labelMap.data;

  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < h; y++) {
    const rowOffset = y * w * 4;
    for (let x = 0; x < w; x++) {
      if (data[rowOffset + x * 4] === label) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < 0) return null;
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/**
 * Copy the rectangle `bbox` out of `src` into a fresh ImageData. The bbox is
 * assumed to be inside the source bounds - `computeLabelBbox` always returns
 * one that is.
 */
export function cropImageData(src: ImageData, bbox: Bbox): ImageData {
  const out = new ImageData(bbox.w, bbox.h);
  const sw = src.width;
  const sd = src.data;
  const dd = out.data;
  const rowBytes = bbox.w * 4;
  for (let y = 0; y < bbox.h; y++) {
    const srcStart = ((y + bbox.y) * sw + bbox.x) * 4;
    const dstStart = y * rowBytes;
    // subarray + set is the fast path on TypedArrays - no per-byte loop in JS.
    dd.set(sd.subarray(srcStart, srcStart + rowBytes), dstStart);
  }
  return out;
}
